import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import Axios from "../utils/axiosInterceptor";
import { DISCHARGE, PATIENT_APIS } from "../utils/constants";

// Define types for the API responses
export interface DiagnosisItem {
  id: number;
  portal: string;
  gsigmaPayerId: number;
  diagnosis: string;
  masterLookId: number;
  payerId: null | number;
  lookupMasterType: number;
  payerMasterId: string | null;
  maMasterValue: null | string;
  masterTableName: null | string;
  maMasterId: number;
  payerParentId: number;
  payerParentValue: null | string;
  payerProductCode: null | string;
  lookupMasterValue: string;
  additional: null | string;
  icd10: null | string;
  icd10PCS: null | string;
}

export interface RoomTypeItem {
  id: number;
  portal: string;
  gsigmaPayerId: number;
  payerProviderId: number;
  providerId: number;
  providerRoomType: string;
  roomRent: number;
  payerRoomTypeId: number;
  roomCategory: string;
  insuranceCompany: string;
  payerId: number;
  productCode: null | string;
  insuranceCompanyId: null | number;
}

export interface ProcedureItem {
  id: number;
  portal: string;
  gsigmaPayerId: number;
  procedure: string;
  masterLookId: number;
  payerId: number;
  lookupMasterType: number;
  payerMasterId: null | string;
  masterTableName: string;
  lookupMasterValue: string;
}

export interface DropdownOption {
  value: string;
  label: string;
}

// Main service class
class DropdownDataService {
  private baseUrl: string;
  private payerName: string | undefined;

  constructor(baseUrl: string = "/api") {
    this.baseUrl = baseUrl;
    this.payerName = "Medi Assist Insurance TPA India Pvt Ltd"
  }

  // Set payer name if needed
  setPayerName(payerName: string): void {
    this.payerName = payerName;
  }

  // Fetch diagnosis data and convert to dropdown options
  async fetchDiagnosisOptions(portalMetaDataId: number, hospitalId: number, schemeId: number, schemeName: string = "Medi Assist Insurance TPA India Pvt Ltd") {
    try {
      const response = await Axios.post(
        `${PATIENT_APIS.GET_DIAGNOSIS_DATA}/portalMetaDataId/${portalMetaDataId}/hospitalId/${hospitalId}/schemeId/${schemeId}`,
        { procedureCooperateMappingId: null }
      );
      const data = response.data;

      // Extract diagnosis items from the nested response
      if (this.payerName) {
        const payerProviders = data.portals?.ihx?.payers;

          // Find the key that partially matches the schemeName
          const matchingPayer = Object.keys(payerProviders || {})?.find(
            (payer) => payer?.toLowerCase()?.includes(schemeName?.toLowerCase())
          );

        //   const diagnosisList = matchingPayer
        //     ? payerProviders[matchingPayer]?.diagnosisList
        //     : [];
        const diagnosisList =
          data.portals?.ihx?.payers?.[this.payerName]?.diagnosisList || [];

        // Convert to dropdown options
        return diagnosisList.map((item: DiagnosisItem) => ({
          value: item.id,
          label: item.diagnosis,
        }));
      }
    } catch (error) {
      console.error("Error fetching diagnosis data:", error);
      return [];
    }
  }

  // Fetch room type data and convert to dropdown options
  async fetchRoomTypeOptions(
    hospitalId: string | number | undefined,
    portalMetaDataId: number,
    schemeId: number,
    schemeName: string = "Medi Assist Insurance TPA India Pvt Ltd"
  ) {
    try {
      const response = await Axios.get(
        `${PATIENT_APIS.GET_ROOMTYPES_DATA}/portalMetaDataId/${portalMetaDataId}/schemeId/${schemeId}/hospitalId/${hospitalId}`
      );
      const data = response.data;
  
      if (this.payerName) {
        const payerProviders = data.portals?.ihx?.payers;
  
        // Find the key that partially matches the schemeName
        // const matchingPayer = Object.keys(payerProviders || {})?.find(
        //   (payer) => payer?.toLowerCase()?.includes(schemeName?.toLowerCase())
        // );
  
        // const roomTypes = matchingPayer
        //   ? payerProviders[matchingPayer]?.roomTypes || []
        //   : [];
        // const generalRoomTypes = matchingPayer
        //   ? payerProviders[matchingPayer]?.generalRoomTypes || []
        //   : [];

        const roomTypes = payerProviders[this.payerName]?.roomTypes || [];
        const generalRoomTypes = payerProviders[this.payerName]?.generalRoomTypes || [];
  
        return {
          roomTypes: roomTypes?.map((item: RoomTypeItem) => ({
            value: item.id,
            label: `${item.providerRoomType}`,
          })),
          generalRoomTypes: generalRoomTypes?.map((item: any) => ({
            value: item.id,
            label: `${item.roomType}`,
          })),
        };
      }
    } catch (error) {
      console.error("Error fetching room type data:", error);
      return { roomTypes: [], generalRoomTypes: [] };
    }
  }
  

  // Fetch procedure data and convert to dropdown options
  async fetchProcedureOptions(
    hospitalId: string | number | undefined,
    portalMetaDataId: number,
    schemeId: number,
    schemeName: string = "Medi Assist Insurance TPA India Pvt Ltd"
  ) {
    try {
      const response = await Axios.get(
        `${PATIENT_APIS.GET_PROCEDURE_DATA}/portalMetaDataId/${portalMetaDataId}/hospitalId/${hospitalId}/schemeId/${schemeId}`
      );

      const data = response.data;

      // Extract procedure items from the nested response
      if(this.payerName) {
        const payerProviders = data.portals?.ihx?.payers;

          // Find the key that partially matches the schemeName
        //   const matchingPayer = Object.keys(payerProviders || {})?.find(
        //     (payer) => payer?.toLowerCase()?.includes(schemeName?.toLowerCase())
        //   );

        //   const procedureList = matchingPayer
        //     ? payerProviders[matchingPayer]?.procedureList
        //     : [];
      const procedureList =
        data.portals?.ihx?.payers?.[this.payerName]?.procedureList || [];

      // Convert to dropdown options
      return procedureList.map((item: ProcedureItem) => ({
        value: item.id,
        label: item.procedure,
      }));
    }
    } catch (error) {
      console.error("Error fetching procedure data:", error);
      return [];
    }
  }

  async fetchTreatmentOptions(
    hospitalId: string | number | undefined,
    portalId: number,
    schemeName: string = "Medi Assist Insurance TPA India Pvt Ltd",
  ) {
    try {
      const response = await Axios.get(
        `${PATIENT_APIS.GET_TREATMENT_DATA}/portalMetaDataId/${portalId}`
      );

      const data = response.data;

      // Extract procedure items from the nested response
      if(this.payerName) {
        const payerProviders = data.portals?.ihx?.payerProviders;

          // Find the key that partially matches the schemeName
        //   const matchingPayer = Object.keys(payerProviders || {})?.find(
        //     (payer) => payer?.toLowerCase()?.includes(schemeName?.toLowerCase())
        //   );

        //   const treatmentList = matchingPayer
        //     ? payerProviders[matchingPayer]?.lineOfTreatments
        //     : [];
      const treatmentList =
        data.portals?.ihx?.payerProviders?.[this.payerName]?.lineOfTreatments ||
        [];

      // Convert to dropdown options
      return treatmentList;
      }
    } catch (error) {
      console.error("Error fetching procedure data:", error);
      return [];
    }
  }

  async fetchChronicIllness(
    hospitalId: string | number | undefined,
    schemeId: string | number | undefined,
    portalMetaDataId: number
  ): Promise<any> {
    try {
      const response = await Axios.get(
        `${PATIENT_APIS.GET_CHRONIC_ILLNESS}/portalMetaDataId/${portalMetaDataId}`
      );

      const data = response.data;

      // Convert to dropdown options
      return data;
    } catch (error) {
      console.error("Error fetching procedure data:", error);
      return [];
    }
  }
}

export const getPreAuthIhx = async (
  caseId: number | undefined,
  visitId: number | undefined
): Promise<any> => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.PREAUTH_DATA}/api/ihx/cases/${caseId}/visitInfo/${visitId}/getAllCasePreAuthByCaseIdAndVisitId`
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot get Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot get Pre Auth details");
  }
};

export const postPreAuthIhx = async (
  caseId: number,
  visitId: number,
  payload: any
): Promise<any> => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.PREAUTH_DATA}/api/ihx/cases/${caseId}/visitInfo/${visitId}/casePreAuth`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot post Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot post Pre Auth details");
  }
};

export const updatePreAuthIhx = async (
  caseId: number,
  visitId: number,
  casePreAuthId: number,
  payload: any
): Promise<any> => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.PREAUTH_DATA}/api/ihx/cases/${caseId}/visitInfo/${visitId}/casePreAuthId/${casePreAuthId}`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot update Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot update Pre Auth details");
  }
};

export const fetchPreAuthToPortal = async (
  caseId: number,
  visitId: number,
  payload: any
): Promise<any> => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.PREAUTH_DATA}/api/ihx/cases/${caseId}/visitInfo/${visitId}/initiateCasePreAuth`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot post Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot post Pre Auth details");
  }
};

export const saveAsDraftCghs = async (caseId: number, visitId: number, formData: any) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.POST_PREAUTH_DATA}/${caseId}/visitInfo/${visitId}/casePreAuth`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot save Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot save Pre Auth details");
  }
}

export const updateAsDraftCghs = async (caseId: number, visitId: number, casePreAuthId: number, formData: any) => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.POST_PREAUTH_DATA}/${caseId}/visitInfo/${visitId}/casePreAuthId/${casePreAuthId}`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot update Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot update Pre Auth details");
  }
}

export const fetchPreAuthIhxToPortal = async (
  caseId: number,
  visitId: number,
  payload: any
): Promise<any> => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.PREAUTH_DATA}/api/ihx/cases/${caseId}/visitInfo/${visitId}/initiateCasePreAuth`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot post Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot post Pre Auth details");
  }
};

export const validationPreAuth = async (
  caseId: number,
  visitId: number,
  formData: any
) => {
  try {
    const res = await Axios.post(
      `${PATIENT_APIS.VALIDATION_PREAUTH}/${caseId}/visitInfo/${visitId}`,
      formData
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to initiate Pre auth");
  }
};

// Create and export a singleton instance
export const dropdownService = new DropdownDataService();

// Export the class as well for testing or custom instantiation
export default DropdownDataService;